import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    useTheme,
    alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
    Assessment,
    FilterList,
    AccountBalanceWallet,
    Visibility,
    Article,
    CurrencyBitcoin,
    TrendingUp,
    Gavel,
    EventAvailable,
    BarChart,
    NotificationsActive,
    AccountBalance,
    AutoAwesome,
    Savings,
    Forum,
    ManageSearch
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

import Portfolio_dark from '../../images/PortfolioNew_dark.png';
import Screener_Dark from '../../images/Screener_Dark.png';
import Watchlist_dark from '../../images/Watchlist_dark.png';
import Crypto_Dark from '../../images/Crypto_Dark.png';
import RevBreakdown_dark from '../../images/RevBreakdown_dark.png';
import CongressMembers_dark from '../../images/CongressMembers_dark.png';
import EarningTranscript_dark from '../../images/EarningTranscript_dark.png';
import EarningCalendar_dark from '../../images/EarningCalendar_DarkV2.png';
import Financials_Dark from '../../images/Financials_Dark.png';
import Ratings_dark from '../../images/Ratings_dark.png';
import AlertSystem_dark from '../../images/AlertSystem_dark.png';
import POTUS_dark from '../../images/POTUS_dark.png';
import DividendCalculator_dark from '../../images/DividendCalculator_Dark.png';
import RedditTracker_dark from '../../images/RedditTracker_Dark.png';
import InsiderTrading_dark from '../../images/InsiderTrading_Dark.png';

import Portfolio_light from '../../images/PortfolioNew_light.png';
import Screener_Light from '../../images/Screener_Light.png';
import Watchlist_light from '../../images/Watchlist_light.png';
import Crypto_Light from '../../images/Crypto_Light.png';
import RevBreakdown_light from '../../images/RevBreakdown_light.png';
import CongressMembers_light from '../../images/CongressMembers_light.png';
import EarningTranscript_light from '../../images/EarningTranscript_light.png';
import EarningCalendar_light from '../../images/EarningCalendar_LightV2.png';
import Financials_Light from '../../images/Financials_Light.png';
import Ratings_light from '../../images/Ratings_light.png';
import AlertSystem_light from '../../images/AlertSystem_light.png';
import POTUS_light from '../../images/POTUS_light.png';
import DividendCalculator_light from '../../images/DividendCalculator_Light.png';
import RedditTracker_light from '../../images/RedditTracker_Light.png';
import InsiderTrading_light from '../../images/InsiderTrading_Light.png';

const fadeSlideUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const features = [
    {
        icon: <Assessment />,
        title: 'Financial Data & Analytics',
        description: "Dive into detailed financial statements including income statements, balance sheets, and cash flow reports. Analyze key metrics TTM covering valuation, returns, leverage, liquidity, and more.",
        imageDark: Financials_Dark,
        imageLight: Financials_Light,
    },
    {
        icon: <AutoAwesome />,
        title: 'RodeoAI: AI Research Agent',
        description: 'Your 24/7 financial analyst powered by real-time data. Ask questions in plain English about stocks, track congressional trades, screen for opportunities, and analyze your portfolio. Get accurate, comprehensive answers in seconds through natural conversation.',
        imageDark: null,
        imageLight: null,
        isVideo: true,
        videoUrl: 'https://www.youtube.com/embed/CAyMK_ycii8?si=38Man4KaUxB7oiZR',
    },
    {
        icon: <NotificationsActive />,
        title: 'Smart Alert System',
        description: 'Get instant notifications for stock movements, congressional disclosures, insider activity, market news and more. Receive alerts via site notifications, email, Telegram, or Discord when your tracked events occur.',
        imageDark: AlertSystem_dark,
        imageLight: AlertSystem_light,
    },
    {
        icon: <AccountBalanceWallet />,
        title: 'Portfolio Management',
        description: 'Track and analyze your entire portfolio in one place. Compare holdings against the S&P 500, monitor financials, analyst ratings, dividend income, and risk metrics. Share publicly or with specific people. Plus AI Portfolio Analysis that scores your portfolio, breaks down every holding, and gives you prioritized recommendations in seconds.',
        imageDark: Portfolio_dark,
        imageLight: Portfolio_light,
    },
    {
        icon: <FilterList />,
        title: 'Advanced Screeners',
        description: "Screen stocks across fundamentals, valuation, cash flow, technicals, and analyst ratings. Over 90 filters across multiple categories, all fully combinable. Don't know where to start? Pre-built screens for popular strategies get you going in one click.",
        imageDark: Screener_Dark,
        imageLight: Screener_Light,
    },
    {
        icon: <BarChart />,
        title: 'Revenue Breakdown',
        description: 'Analyze how companies generate revenue with detailed breakdowns by segments, regions, and product lines. Gain insights into business diversification and key income drivers.',
        imageDark: RevBreakdown_dark,
        imageLight: RevBreakdown_light,
    },
    {
        icon: <AccountBalance />,
        title: 'POTUS Tracker',
        description: 'Monitor presidential activities and their market impact in real-time. Track executive orders, Truth Social posts, and analyze how major sectors perform across multiple timeframes since inauguration.',
        imageDark: POTUS_dark,
        imageLight: POTUS_light,
    },
    {
        icon: <Gavel />,
        title: 'Congress Tracker',
        description: 'Track and analyze the trading activities of all members of Congress. Explore detailed trading histories, patterns, and financial moves.',
        imageDark: CongressMembers_dark,
        imageLight: CongressMembers_light,
    },
    {
        icon: <ManageSearch />,
        title: 'Insider Trading Activity',
        description: "Track insider trading activities of corporate executives, directors, and major shareholders. See exactly what company insiders are buying and selling, when they filed, and at what price across thousands of transactions.",
        imageDark: InsiderTrading_dark,
        imageLight: InsiderTrading_light,
    },
    {
        icon: <TrendingUp />,
        title: 'Ratings & Metrics',
        description: 'Evaluate companies with in-depth ratings, financial health scores, and key performance metrics. Compare valuations, risk factors, and overall market positioning.',
        imageDark: Ratings_dark,
        imageLight: Ratings_light,
    },
    {
        icon: <Visibility />,
        title: 'Watchlist Tracking',
        description: 'Keep an eye on your favorite stocks, crypto, and indexes with a personalized watchlist. Easily monitor price movements and key data at a glance.',
        imageDark: Watchlist_dark,
        imageLight: Watchlist_light,
    },
    {
        icon: <CurrencyBitcoin />,
        title: 'Cryptocurrencies',
        description: 'Track crypto prices in real-time with advanced cryptocurrency research and market analysis tools.',
        imageDark: Crypto_Dark,
        imageLight: Crypto_Light,
    },
    {
        icon: <Savings />,
        title: 'Dividend Calculator',
        description: "Build a custom dividend portfolio and instantly calculate your expected income with live market data. See annual, quarterly, and monthly income at a glance. Model future growth with DRIP reinvestment, adjustable time horizons up to 30 years, and auto-populated growth rates with year-by-year projections.",
        imageDark: DividendCalculator_dark,
        imageLight: DividendCalculator_light,
    },
    {
        icon: <Article />,
        title: 'Market Reports',
        description: 'Access detailed market reports, earnings transcripts, and analyst insights. Stay informed with professional research and analysis.',
        imageDark: EarningTranscript_dark,
        imageLight: EarningTranscript_light,
    },
    {
        icon: <Forum />,
        title: 'Reddit Stock Tracker',
        description: "Track what retail investors are discussing across Reddit's top investing communities. See which stocks are trending, analyze bullish or bearish sentiment, and compare discussion patterns from aggressive traders to value investors. Spot trends before they hit mainstream news.",
        imageDark: RedditTracker_dark,
        imageLight: RedditTracker_light,
    },
    {
        icon: <EventAvailable />,
        title: 'Market Calendars',
        description: 'Stay ahead with our comprehensive market calendars, including earnings reports, key economic events, and upcoming dividend schedules.',
        imageDark: EarningCalendar_dark,
        imageLight: EarningCalendar_light,
    },
];

const FeatureRow = ({ feature, index }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isEven = index % 2 === 0;

    const mediaContent = feature.isVideo ? (
        <Box
            sx={{
                position: 'relative',
                paddingTop: '56.25%',
                borderRadius: 3,
                overflow: 'hidden',
            }}
        >
            <iframe
                src={feature.videoUrl}
                title={feature.title}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '12px',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </Box>
    ) : (
        <Box
            component="img"
            src={isDark ? feature.imageDark : feature.imageLight}
            alt={feature.title}
            loading="lazy"
            sx={{
                width: '100%',
                height: 'auto',
                borderRadius: 3,
                display: 'block',
            }}
        />
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: {
                    xs: 'column',
                    md: isEven ? 'row' : 'row-reverse',
                },
                alignItems: 'center',
                gap: { xs: 4, md: 8 },
                py: { xs: 6, md: 10 },
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                '&:last-child': {
                    borderBottom: 'none',
                },
            }}
        >
            {/* Text side */}
            <Box
                sx={{
                    flex: '1 1 35%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}>
                        {feature.icon}
                    </Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                            lineHeight: 1.2,
                            color: isDark ? '#fff' : theme.palette.text.primary,
                        }}
                    >
                        {feature.title}
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        lineHeight: 1.7,
                        color: 'text.secondary',
                        maxWidth: '480px',
                    }}
                >
                    {feature.description}
                </Typography>
            </Box>

            {/* Image/Video side */}
            <Box sx={{ flex: '1 1 60%', width: '100%' }}>
                {mediaContent}
            </Box>
        </Box>
    );
};

const Features = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box>
            <Helmet>
                <title>Features | Market Rodeo - Your entire research, in one place.</title>
                <meta name="description" content="Stock screener with 90+ filters, AI-powered research, congressional and insider trade tracking, portfolio management, dividend calculator, real-time alerts and more. Start free." />
                <meta property="og:title" content="Features | Market Rodeo" />
                <meta property="og:description" content="Stock screener with 90+ filters, AI-powered research, congressional and insider trade tracking, portfolio management, dividend calculator, real-time alerts and more. Start free." />
            </Helmet>
            {/* Intro Hero */}
            <Box
                sx={{
                    py: { xs: 8, md: 14 },
                    textAlign: 'center',
                    background: isDark
                        ? 'linear-gradient(180deg, rgba(25,118,210,0.05) 0%, transparent 100%)'
                        : 'linear-gradient(180deg, rgba(25,118,210,0.03) 0%, transparent 100%)',
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            color: theme.palette.primary.main,
                            mb: 2,
                            animation: `${fadeSlideUp} 0.6s ease both`,
                        }}
                    >
                        Features
                    </Typography>

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '2.25rem', sm: '3rem', md: '3.75rem' },
                            lineHeight: 1.15,
                            mb: 3,
                            color: isDark ? '#fff' : theme.palette.text.primary,
                            animation: `${fadeSlideUp} 0.6s ease 0.1s both`,
                        }}
                    >
                        Your entire research, in one place.
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', md: '1.2rem' },
                            lineHeight: 1.7,
                            color: 'text.secondary',
                            maxWidth: '600px',
                            mx: 'auto',
                            mb: 5,
                            animation: `${fadeSlideUp} 0.6s ease 0.2s both`,
                        }}
                    >
                        Professional-grade market research tools designed for every investor.
                        Everything you need to make smarter decisions, in one place.
                    </Typography>

                    <Box sx={{ animation: `${fadeSlideUp} 0.6s ease 0.3s both` }}>
                        <Link to="/signup" style={{ textDecoration: 'none' }}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{
                                    py: 1.75,
                                    px: 6,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                                    boxShadow: '0 4px 20px rgba(25,118,210,0.35)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 24px rgba(25,118,210,0.45)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Sign Up For Free
                            </Button>
                        </Link>
                    </Box>
                </Container>
            </Box>

            {/* Section header */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: { xs: 6, md: 8 },
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    background: isDark
                        ? alpha(theme.palette.primary.main, 0.03)
                        : alpha(theme.palette.primary.main, 0.02),
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                            mb: 2,
                            color: isDark ? '#fff' : theme.palette.text.primary,
                        }}
                    >
                        Every tool. One platform.
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            color: 'text.secondary',
                            lineHeight: 1.6,
                        }}
                    >
                        Everything you need to research, track, and act on market opportunities.
                        See more of what matters to you, in less time.
                    </Typography>
                </Container>
            </Box>

            {/* Feature rows */}
            <Container maxWidth="xl">
                {features.map((feature, index) => (
                    <FeatureRow key={index} feature={feature} index={index} />
                ))}
            </Container>

            {/* Bottom CTA */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: { xs: 8, md: 12 },
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    background: isDark
                        ? 'linear-gradient(180deg, transparent 0%, rgba(25,118,210,0.05) 100%)'
                        : 'linear-gradient(180deg, transparent 0%, rgba(25,118,210,0.03) 100%)',
                }}
            >
                <Container maxWidth="sm">
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                            mb: 2,
                            color: isDark ? '#fff' : theme.palette.text.primary,
                        }}
                    >
                        Ready to get started?
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            color: 'text.secondary',
                            mb: 4,
                        }}
                    >
                        Join thousands of investors already using Market Rodeo.
                    </Typography>
                    <Link to="/signup" style={{ textDecoration: 'none' }}>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{
                                py: 1.75,
                                px: 6,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                                boxShadow: '0 4px 20px rgba(25,118,210,0.35)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 24px rgba(25,118,210,0.45)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            Sign Up For Free
                        </Button>
                    </Link>
                </Container>
            </Box>
        </Box>
    );
};

export default Features;